// BankAccountAuthentication.tsx
import React, { useEffect, useState } from 'react';
import axios from "../lib/axios";
import { useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Banknote, Info, AlertCircle } from "lucide-react";
import { useIsMobile } from "../hooks/use-mobile";
import { API_BASE_URL } from '../config';
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { consentTrue, bankAccountString, ifscString, requiredString } from "../helpers/validations";
import { useLoader } from "../contexts/LoaderContext";
import { useJourney } from "../contexts/JourneyContext";
import CallbackHelp from "../components/CallbackHelp";
import { toast } from "sonner";
import JourneyHeader from "@/components/JourneyHeader";

const finalOfferSubmitSchema = z.object({
    beneficiary_account_no: bankAccountString("Beneficiary Account Number") as z.ZodType<string>,
    beneficiary_ifsc: ifscString("Beneficiary IFSC Code") as z.ZodType<string>,
    account_type: requiredString("Account Type") as z.ZodType<string>,
    bank_balance_promise: consentTrue("Acceptance of Bank Balance Assurance") as z.ZodType<boolean>,
    allow_lending_entity_credit_review: consentTrue("Acceptance of Terms & Privacy Policy") as z.ZodType<boolean>,
});

type FormValues = {
    beneficiary_account_no: string;
    beneficiary_ifsc: string;
    account_type: string;
    bank_balance_promise: boolean;
    allow_lending_entity_credit_review: boolean;
};

interface BankAccountAuthenticationProps {
    isBuilderMode?: boolean;
    registerSubmit?: (handler: () => Promise<boolean>) => void;
    onDone?: () => void;
    // Reports the 4 mandatory inputs up to JourneyPage so the footer Continue
    // button can stay greyed out until they are all filled/ticked.
    onChange?: (val: Record<string, any>) => void;
}

const BankAccountAuthentication = ({ isBuilderMode = false, registerSubmit, onDone, onChange }: BankAccountAuthenticationProps) => {
    const navigate = useNavigate();
    const { refresh: refreshJourney } = useJourney();
    const [isCallbackOpen, setIsCallbackOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isConfirm2Open, setIsConfirm2Open] = useState(false);
    const [fetching, setFetching] = useState(true);
    const { loading, setLoading, setLoaderMessage } = useLoader();
    const [content, setContent] = useState<string | null>(null);
    const [bankBalCheckLabel, setBankBalCheckLabel] = useState<string | null>(null);
    const [consentDetails, setConsentDetails] = useState<string | null>(null);
    const [bankBalancePromiseDetails, setBankBalancePromiseDetails] = useState<string | null>(null);
    const isMobile = useIsMobile();

    // BRE2 bank account hints state
    const [maskedAccounts, setMaskedAccounts] = useState<string[]>([]);
    const [hasHints, setHasHints] = useState(false);
    const [accountLast4Error, setAccountLast4Error] = useState<string | null>(null);

    const form = useForm<FormValues>({
        resolver: zodResolver(finalOfferSubmitSchema),
        mode: "onChange",
        reValidateMode: "onChange",
        shouldUnregister: true,
        defaultValues: {
            bank_balance_promise: false,
            allow_lending_entity_credit_review: false,
            account_type: "savings"
        },
    });

    // Watch account number for live last-4-digit validation
    const watchedAccountNo = form.watch("beneficiary_account_no");

    useEffect(() => {
        if (!hasHints || !watchedAccountNo) {
            setAccountLast4Error(null);
            return;
        }
        const inputLast4 = watchedAccountNo.trim().slice(-4);
        if (inputLast4.length < 4) {
            setAccountLast4Error(null);
            return;
        }
        const matches = maskedAccounts.some(
            (masked) => masked.trim().slice(-4) === inputLast4
        );
        if (!matches) {
            setAccountLast4Error(
                `Account number must end with one of: ${maskedAccounts.map(a => a.slice(-4)).join(', ')}`
            );
        } else {
            setAccountLast4Error(null);
        }
    }, [watchedAccountNo, maskedAccounts, hasHints]);

    useEffect(() => {
        setFetching(true);

        // Fetch bank details and hints in parallel
        Promise.all([
            axios.post(`${API_BASE_URL}/api/get_bank_details/`, { maxRedirects: 0 }),
            axios.get(`${API_BASE_URL}/api/caprion/bank-account-hints/`).catch(() => null),
        ])
            .then(([bankRes, hintsRes]) => {
                if (bankRes.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
                    navigate("/session-timeout");
                    return;
                }

                if (bankRes.status === 200) {
                    form.reset({
                        ...bankRes.data,
                        account_type: bankRes.data?.account_type || "savings",
                    } as FormValues);

                    // Fetch consent details
                    axios.post(`${API_BASE_URL}/api/get_final_offer_details/`, {}).then(res_consent => {
                        setContent(res_consent.data["content"]);
                        setBankBalCheckLabel(res_consent.data["bank_bal_label_content"]);
                        setConsentDetails(res_consent.data["consent_details"]);
                        setBankBalancePromiseDetails(res_consent.data["bank_balance_promise_details"]);
                    });
                }

                // Process hints response
                if (hintsRes && hintsRes.status === 200 && hintsRes.data?.has_hints) {
                    setMaskedAccounts(hintsRes.data.masked_accounts || []);
                    setHasHints(true);
                }
            })
            .catch((err) => console.error("Fetch error:", err))
            .finally(() => setFetching(false));
    }, []);

    // Register submit handler for Journey Continue button
    useEffect(() => {
        if (registerSubmit) {
            registerSubmit(async () => {
                // Validate form first
                const isValid = await form.trigger();
                if (!isValid) {
                    toast.error("Please fill all required fields correctly");
                    return false;
                }
                // Block if last-4 validation fails
                if (accountLast4Error) {
                    toast.error(accountLast4Error);
                    return false;
                }
                // Show confirmation dialog, then execute
                setIsConfirmOpen(true);
                // Return a promise that resolves when user confirms/cancels
                return new Promise((resolve) => {
                    // Store the resolve function to call it from dialog actions
                    (window as any).__bankDetailsConfirmResolve = resolve;
                });
            });
        }
    }, [registerSubmit, form, accountLast4Error]);

    const handleConfirmOffer = async (): Promise<boolean> => {
        setIsConfirmOpen(false);
        const resolve = (window as any).__bankDetailsConfirmResolve;
        setLoading(true);
        setLoaderMessage("bank-confirm");
        const values = form.getValues();
        const data = {
            ...values,
            consentDetails,
            bankBalancePromiseDetails,
            'offerConfirmed': true,
        }

        try {
            const res = await axios.post(`${API_BASE_URL}/api/set_bank_details/`, data, { maxRedirects: 0 });
            
            if (res.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
                navigate("/session-timeout");
                return false;
            }
            
            if (res.status === 200) {
                try {
                    await refreshJourney();
                } catch (e) {
                    console.error("Failed to refresh journey state:", e);
                }

                // If onDone callback exists (journey mode), call it
                if (onDone) {
                    onDone();
                }
                // Resolve the promise for registerSubmit
                if (resolve) {
                    resolve(true);
                    delete (window as any).__bankDetailsConfirmResolve;
                }
                return true;
            }
            return false;
        } catch (err: any) {
            toast.error("Error saving data: " + (err.response?.data?.error || err.message));
            // Resolve the promise for registerSubmit
            if (resolve) {
                resolve(false);
                delete (window as any).__bankDetailsConfirmResolve;
            }
            return false;
        } finally {
            setLoaderMessage(null);
            setLoading(false);
        }
    };

    const rejectOfferDeleteData = () => {
        setLoading(true);
        axios
            .post(`${API_BASE_URL}/api/delete_user/`, {})
            .then((res) => {
                if (res.status === 200) {
                    navigate("/")
                }
            })
            .catch((err) => {
                if (confirm("Error deleting data: " + err.message + ".\n Press OK to reload the page.")) {
                    window.location.reload();
                }
            })
            .finally(() => setLoading(false));
    };

    // Derived: is the Continue/Confirm button disabled due to last-4 mismatch?
    const isLast4Invalid = hasHints && !!accountLast4Error;

    // Publish gate state to JourneyPage. These 4 field names are listed in this
    // page's layout.required_fields, so the journey Continue button stays greyed
    // out until both inputs have a value and both consent boxes are ticked.
    // Format/last-4 errors are still enforced separately by the submit handler.
    const watchedIfsc = form.watch("beneficiary_ifsc");
    const watchedBankBalancePromise = form.watch("bank_balance_promise");
    const watchedCreditReview = form.watch("allow_lending_entity_credit_review");

    useEffect(() => {
        onChange?.({
            beneficiary_account_no: watchedAccountNo || '',
            beneficiary_ifsc: watchedIfsc || '',
            bank_balance_promise: watchedBankBalancePromise === true,
            allow_lending_entity_credit_review: watchedCreditReview === true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedAccountNo, watchedIfsc, watchedBankBalancePromise, watchedCreditReview]);

    if (fetching) return null;

    return (
        <div className="min-h-screen bg-intellend-white">
            <JourneyHeader />

            <main className="container mx-auto px-4 py-8 max-w-3xl">
                <div className="bg-white shadow-md rounded-lg p-6 mb-8">
                    <div className="text-2xl font-bold text-intellend-green mb-6 text-center">Bank Account Details</div>

                    {/* BRE2 Account Hints Banner */}
                    {hasHints && (
                        <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-2">
                            <div className="flex items-start gap-2">
                                <Info className="mt-0.5 shrink-0 text-blue-600" size={16} />
                                <div className="text-sm text-blue-800">
                                    <span className="font-semibold">Please enter the account number ending with one of these:</span>{" "}
                                    {maskedAccounts.map((acc, i) => (
                                        <span key={acc}>
                                            <span className="font-mono font-bold">{acc}</span>
                                            {i < maskedAccounts.length - 1 && ", "}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <Info className="mt-0.5 shrink-0 text-blue-600" size={16} />
                                <p className="text-sm text-blue-800">
                                    Please use an account that is linked to a UPI ID.
                                </p>
                            </div>
                        </div>
                    )}

                    <Form {...form}>
                        <form className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="beneficiary_account_no"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Bank Account Number <span className="text-red-500">*</span></FormLabel>
                                            <FormControl>
                                                <Input placeholder="Beneficiary Account Number" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                            {/* Last-4 digit mismatch error */}
                                            {accountLast4Error && (
                                                <div className="flex items-center gap-1.5 mt-1">
                                                    <AlertCircle size={14} className="text-red-500 shrink-0" />
                                                    <p className="text-xs text-red-500">{accountLast4Error}</p>
                                                </div>
                                            )}
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="beneficiary_ifsc"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>IFSC Code <span className="text-red-500">*</span></FormLabel>
                                            <FormControl>
                                                <Input placeholder="IFSC Code" {...field} className="uppercase h-9" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="account_type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Account Type <span className="text-red-500">*</span></FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value} disabled>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select Account Type" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="savings">Saving Account</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <p className="text-sm font-bold text-gray-700">Only savings account accepted</p>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div
                                className="border p-4 rounded-md bg-gray-50 text-sm"
                                dangerouslySetInnerHTML={{ __html: bankBalCheckLabel || "" }}
                            />

                            <FormField
                                control={form.control}
                                name="bank_balance_promise"
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                        <div className="flex gap-2 items-center">
                                            <input id="bank_balance_promise_check" type="checkbox" checked={field.value} onChange={field.onChange} className="h-4 w-4" />
                                            <FormLabel htmlFor="bank_balance_promise_check" className="text-sm cursor-pointer">
                                                I acknowledge and consent
                                            </FormLabel>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div
                                className="border p-4 rounded-md bg-gray-50 text-sm"
                                dangerouslySetInnerHTML={{ __html: content || "" }}
                            />

                            <FormField
                                control={form.control}
                                name="allow_lending_entity_credit_review"
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                        <div className="flex gap-2 items-center">
                                            <input id="allow_lending_entity_credit_review_check" type="checkbox"
                                                checked={field.value} onChange={field.onChange} className="h-4 w-4" />
                                            <FormLabel htmlFor="allow_lending_entity_credit_review_check" className="text-sm cursor-pointer">
                                                I acknowledge and consent
                                            </FormLabel>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="flex flex-col md:flex-row gap-4 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsCallbackOpen(true)}
                                    className="w-full md:w-auto"
                                >
                                    Request Callback
                                </Button>
                                {!registerSubmit && (
                                    <Button
                                        type="button"
                                        className="w-full md:w-auto md:ml-auto bg-intellend-green hover:bg-intellend-green/90"
                                        onClick={() => setIsConfirmOpen(true)}
                                        disabled={loading || !form.formState.isValid || isLast4Invalid}
                                    >
                                        <Banknote className="mr-2" size={18} />
                                        Confirm Bank Details
                                    </Button>
                                )}
                            </div>

                            <div className="flex flex-col md:flex-row gap-4 pt-2">
                                <Button
                                    type="button"
                                    className="w-full border border-red-600 text-red-600 bg-transparent px-4 py-2 rounded hover:bg-red-50"
                                    onClick={() => setIsConfirm2Open(true)}
                                >
                                    Reject Offer & Delete Data
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </main>

            <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Bank Details</AlertDialogTitle>
                        <AlertDialogDescription>
                            Please ensure the bank details provided are accurate for loan disbursal.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <Button variant="outline" onClick={() => {
                            setIsConfirmOpen(false);
                            const resolve = (window as any).__bankDetailsConfirmResolve;
                            if (resolve) {
                                resolve(false);
                                delete (window as any).__bankDetailsConfirmResolve;
                            }
                        }}>Cancel</Button>
                        <AlertDialogAction onClick={handleConfirmOffer} className="bg-intellend-green hover:bg-intellend-green/90">
                            Confirm
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={isConfirm2Open} onOpenChange={setIsConfirm2Open}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reject Loan Offer</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will delete all the data stored with Intellend.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <Button variant="outline" onClick={() => setIsConfirm2Open(false)}>Cancel</Button>
                        <AlertDialogAction onClick={rejectOfferDeleteData} className="bg-intellend-green hover:bg-intellend-green/90">
                            Reject & Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <CallbackHelp
              isOpen={isCallbackOpen}
              setIsOpen={setIsCallbackOpen}
              showOk={false}
              showCallUs={true}
              showWhatsappUs={true}
              silentlyTrigger={true}
              title="Intellend Support"
            />
        </div>
    );
};

export default BankAccountAuthentication;
